import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import * as React from "react";

import { cn } from "@/lib/utils";

function ButtonLoadingDots() {
  return (
    <span className="tw:inline-flex tw:items-center tw:gap-1">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="tw:size-1.5 tw:animate-pulse tw:rounded-full tw:bg-current"
          style={{ animationDelay: `${index * 160}ms` }}
        />
      ))}
    </span>
  );
}

function ButtonContent({
  children,
  loading,
}: {
  children: React.ReactNode;
  loading: boolean;
}) {
  return (
    <>
      {loading && (
        <span className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
          <ButtonLoadingDots />
        </span>
      )}
      <span
        className={cn(
          "tw:inline-flex tw:items-center tw:justify-center tw:gap-2",
          loading && "tw:opacity-0",
        )}
      >
        {children}
      </span>
    </>
  );
}

const buttonVariants = cva(
  "tw:relative tw:inline-flex tw:items-center tw:justify-center tw:gap-2.5 tw:whitespace-nowrap tw:rounded-md tw:text-base tw:font-medium tw:transition-[color,box-shadow] tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:[&_svg]:pointer-events-none tw:[&_svg:not([class*=size-])]:size-5 tw:[&_svg]:shrink-0 tw:ring-ring/10 tw:dark:ring-ring/20 tw:dark:outline-ring/40 tw:outline-ring/50 tw:focus-visible:ring-4 tw:focus-visible:outline-1 tw:aria-invalid:focus-visible:ring-0",
  {
    variants: {
      variant: {
        default:
          "tw:bg-primary tw:text-primary-foreground tw:shadow-sm tw:hover:bg-primary/90",
        destructive:
          "tw:bg-destructive tw:text-destructive-foreground tw:shadow-xs tw:hover:bg-destructive/90",
        outline:
          "tw:border tw:border-input tw:bg-background tw:shadow-xs tw:hover:bg-accent tw:hover:text-accent-foreground",
        secondary:
          "tw:bg-secondary tw:text-secondary-foreground tw:shadow-xs tw:hover:bg-secondary/80",
        ghost: "tw:hover:bg-accent tw:hover:text-accent-foreground",
        link: "tw:text-primary tw:underline-offset-4 tw:hover:underline",
      },
      size: {
        default: "tw:h-11 tw:px-5 tw:py-2.5 tw:has-[>svg]:px-4",
        sm: "tw:h-10 tw:rounded-md tw:px-4 tw:text-sm tw:has-[>svg]:px-3",
        lg: "tw:h-12 tw:rounded-md tw:px-8 tw:text-lg tw:has-[>svg]:px-5",
        icon: "tw:size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  disabled,
  loading = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const isDisabled = disabled || loading;

  if (asChild) {
    const child = React.Children.only(children);

    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) {
      return null;
    }

    return (
      <Comp
        data-slot="button"
        data-loading={loading}
        aria-busy={loading}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={isDisabled}
        {...props}
      >
        {React.cloneElement(
          child,
          undefined,
          <ButtonContent loading={loading}>
            {child.props.children}
          </ButtonContent>,
        )}
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      data-loading={loading}
      aria-busy={loading}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={isDisabled}
      {...props}
    >
      <ButtonContent loading={loading}>{children}</ButtonContent>
    </Comp>
  );
}

export { Button, buttonVariants };
